// Raiz de la SPA. Sprint 1: F-01 (registro del caso), F-02 (expediente y avisos)
// y las tres entregas de MG-ILE-471: entrada simulada con rol (T-39), bandeja de
// casos (T-37) y visor del PDF en el expediente (T-38).
//
// Solo se montan los modulos que ya tienen pantalla. Un modulo del menu sin pantalla
// cae en `SinPantallaTodavia`, que dice que se puede usar hoy en vez de abrir una cola
// vacia. El Dashboard estuvo en esa lista hasta HU-34b (MG-ILE-1092, Sprint 3), que lo
// construye: es la pantalla de aterrizaje del Administrador (migracion 0043).

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Construction, Inbox } from 'lucide-react';
import AppShell from './components/AppShell';
import RegistroConectado from './screens/RegistroConectado';
import Destinatarios from './screens/Destinatarios';
import Administracion from './screens/Administracion';
import Expediente from './screens/Expediente';
import Bandeja from './screens/Bandeja';
import Indicadores from './screens/Indicadores';
import Dashboard from './screens/Dashboard';
import Trazabilidad from './screens/Trazabilidad';
import BandejaDelComite from './screens/BandejaDelComite';
import EvaluacionDelCaso from './screens/EvaluacionDelCaso';
import type { PestanaDeLaEvaluacion } from './screens/EvaluacionDelCaso';
import MisInvitaciones from './screens/MisInvitaciones';
import BandejaRegulatoria from './screens/BandejaRegulatoria';
import AprobacionDeInformes from './screens/AprobacionDeInformes';
import type { DecisionDelAprobador } from './components/AprobacionDelInforme';
import DisposicionesYSeguimiento from './screens/DisposicionesYSeguimiento';
import LoginSimulado from './screens/LoginSimulado';
import Entrada from './screens/Entrada';
import SinAcceso from './screens/SinAcceso';
import { ProveedorDeCatalogos } from './lib/CatalogosContexto';
import { Button, EmptyState, Modal } from './components/ui';
import { MODULOS, esModuloConocido, modulosDelMenu } from './data/navegacion';
import type { ModuloDeNavegacion } from './data/navegacion';
import { tieneRol } from './lib/sesion';
import type { UsuarioSimulado } from './lib/sesion';
import type { SesionDelServidor } from './api/sesion';
import { useSesion } from './lib/useSesion';
import { MENSAJE_DE_SESION_CADUCADA } from './api/cliente';
import { aprobacionDesdeLaRuta, borradorDesdeLaRuta, casoDesdeLaRuta, olvidarRutaDeLaAprobacion, olvidarRutaDelBorrador, recordarRutaDelBorrador } from './lib/rutaProfunda';
import { borradorDeLaPestana, olvidarBorradorDeLaPestana, recordarEnLaPestana } from './lib/borradorLocal';
import { olvidarFiltros } from './lib/filtrosPersistidos';
import { guardarVista, olvidarVista, vistaInicial } from './lib/vistaPersistida';

// QUE EXPEDIENTE SE MUESTRA. El abierto desde la bandeja manda mientras exista; si
// no, el del caso recien enviado. Estaba escrito dentro de la cascada de modulos de
// `App`, y sus decisiones anidadas —cual caso, si abre con las etapas del nacimiento
// y con que numero— hacian que `App` cruzara el limite de complejidad cognitiva que
// vigila el analisis de calidad. Aqui se leen las tres de corrido.
/** Lo que devuelve el servidor al enviar un caso: su id y su numero definitivo. */
type CasoEnviado = { caso_id: string; numero?: string };

/**
 * LAS VISTAS NO SON LOS MODULOS DEL MENU.
 *
 * `ModuloDeNavegacion` son las entradas del menu lateral. `expediente` y
 * `destinatarios` son pantallas a las que se llega DESDE dentro -al enviar un caso, o
 * por el atajo "Salir" del registro- y que a proposito no tienen entrada propia: el
 * expediente se abre por un caso concreto y los destinatarios son un atajo a una
 * pantalla de Administracion. La distincion estaba en el codigo sin nombrarse; al
 * tipar el estado salio sola, porque `setModulo('expediente')` no encajaba en la
 * lista del menu.
 */
type VistaDeLaAplicacion = ModuloDeNavegacion | 'expediente' | 'destinatarios' | 'evaluacion_del_caso' | 'trazabilidad';

/** True si la vista tiene entrada propia en el menu lateral. */
const ES_DEL_MENU = (v: VistaDeLaAplicacion): v is ModuloDeNavegacion =>
  v !== 'expediente' && v !== 'destinatarios' && v !== 'evaluacion_del_caso' && v !== 'trazabilidad';

/**
 * EL NOMBRE DE LA VISTA DESDE LA QUE SE ABRIO EL EXPEDIENTE.
 *
 * Al expediente se llega desde cinco sitios (la bandeja, la del Comité, la de Asuntos
 * Regulatorios, la cola del aprobador y la evaluación del caso). «Volver» tiene que
 * devolver a AQUEL, no a un destino fijo: el operador lo pidió el 25-sep-2026 después de
 * abrir un expediente desde Regulatorio y quedarse sin salida.
 */
const etiquetaDeLaVista = (v: VistaDeLaAplicacion): string => {
  if (ES_DEL_MENU(v)) return MODULOS[v].label;
  return v === 'evaluacion_del_caso' ? 'Evaluación del caso' : 'Destinatarios';
};

function PanelDeExpediente({ expedienteDe, casoEnviado, onIrABandeja, volverA, onVolver, onVerCompromisos, onVerTrazabilidad }: {
  /** El expediente abierto desde la bandeja. Manda mientras exista. */
  expedienteDe: string | null;
  casoEnviado: CasoEnviado | null;
  onIrABandeja: () => void;
  /** Cómo se llama la vista de la que se vino; `null` si se llegó por el envío. */
  volverA: string | null;
  onVolver: (() => void) | undefined;
  /** HU-27: «Ver compromisos» de la condición C-08 lleva a Disposiciones y seguimiento del caso. */
  onVerCompromisos?: (casoId: string, numero: string) => void;
  /** HU-37: «Ver trazabilidad» del caso abierto (`expediente-ver-trazabilidad`). */
  onVerTrazabilidad?: (casoId: string) => void;
}) {
  const casoId = expedienteDe ?? casoEnviado?.caso_id;

  if (!casoId) {
    return (
      <EmptyState
        icon={Construction}
        title="Todavía no hay un caso enviado en esta sesión"
        description="El expediente se crea al enviar el caso. Desde la bandeja puedes abrir el de cualquier caso ya enviado."
      />
    );
  }

  return (
    <Expediente
      casoId={casoId}
      // Recien enviado desde ESTA sesion: la pantalla abre con las etapas del
      // nacimiento (T-42). Al llegar desde la bandeja (expedienteDe) no: ahi
      // es consulta, no cierre.
      recienEnviado={!expedienteDe && Boolean(casoEnviado)}
      // El numero definitivo viene en la respuesta del envio: la etapa "Caso
      // registrado" lo muestra desde el primer segundo, sin esperar al GET.
      numeroDeCaso={!expedienteDe ? casoEnviado?.numero : undefined}
      // T-42: la espera no secuestra - desde las etapas se sale a la bandeja.
      onIrABandeja={onIrABandeja}
      // «Volver» solo cuando se ABRIO un expediente: al caer aqui por haber enviado un
      // caso no se viene de ninguna pantalla, y ese camino ya sale por «Ir a la bandeja».
      volverA={expedienteDe ? volverA : null}
      onVolver={expedienteDe ? onVolver : undefined}
      onVerCompromisos={onVerCompromisos}
      onVerTrazabilidad={onVerTrazabilidad ? () => onVerTrazabilidad(casoId) : undefined}
    />
  );
}

// Un modulo sin pantalla todavia: se dice cual es, que puede usar ESTA persona hoy
// —los modulos que el servidor le permite y ya tienen pantalla— y se ofrece la salida mas util.
function SinPantallaTodavia({ vista, sesion, onIrA }: {
  vista: VistaDeLaAplicacion;
  sesion: SesionDelServidor;
  onIrA: (modulo: ModuloDeNavegacion) => void;
}) {
  const permitidos = modulosDelMenu(sesion.modulos_permitidos);
  const etiqueta = ES_DEL_MENU(vista) ? MODULOS[vista].label : vista;
  const disponibles = permitidos.filter((k) => MODULOS[k].disponible !== false).map((k) => MODULOS[k].label);
  const inicial = esModuloConocido(sesion.modulo_inicial) ? sesion.modulo_inicial : 'bandeja';
  const bandeja: ModuloDeNavegacion = permitidos.includes('bandeja') ? 'bandeja' : inicial;
  const roles = sesion.roles.map((r) => r.texto_del_rol).join(', ');
  return (
    <EmptyState
      principal
      icon={Construction}
      title={`«${etiqueta}» todavía no está disponible`}
      description={`Hoy, como ${roles}, puedes usar: ${disponibles.join(', ')}.`}
      action={<Button icon={Inbox} onClick={() => onIrA(bandeja)}>Ir a la bandeja</Button>}
    />
  );
}


/**
 * La aplicacion con la sesion ya resuelta (HU-32a, 32a-6): el menu, el aterrizaje y la cabecera
 * salen de `sesion` (`GET /sesion`); `usuario` es la persona en la forma que leen las pantallas.
 * Se monta una vez por sesion: el estado inicial (modulo, caso abierto) se decide al montar.
 */
function AplicacionConSesion({ sesion, usuario, onCerrarSesion }: {
  sesion: SesionDelServidor;
  usuario: UsuarioSimulado;
  onCerrarSesion: () => void;
}) {
  const permitidos = sesion.modulos_permitidos;
  // EL ATERRIZAJE de la persona: el `modulo_inicial` del servidor (el mismo respaldo que
  // `vistaInicial`). Reemplaza a `LANDING[rol]`, la lista que tenía el frontend.
  const aterrizaje: ModuloDeNavegacion = [sesion.modulo_inicial, permitidos[0]].find(esModuloConocido) ?? 'bandeja';
  // LA RUTA PROFUNDA DEL BORRADOR (BUG-370): `?vista=registro&caso=<uuid>`. Se lee UNA vez
  // al montar; manda sobre la vista guardada porque nombra el borrador exacto que estaba
  // abierto. Ver `lib/rutaProfunda.ts`.
  const [borradorDeLaUrl] = useState(() => borradorDesdeLaRuta(window.location.search));
  // LA VISTA SOBREVIVE A RECARGAR (F5). Con sesión ya elegida, se arranca donde se estaba
  // —o en el aterrizaje del rol— y no en el formulario de registro, que era lo que veía
  // el Comité al refrescar (QA exploratorio, 14-sep-2026). Ver `lib/vistaPersistida.ts`.
  const [vistaGuardada] = useState(() => vistaInicial(permitidos, sesion.modulo_inicial));
  const [modulo, setModulo] = useState<VistaDeLaAplicacion>(() => {
    if (borradorDeLaUrl && permitidos.includes('registro')) return 'registro';
    return (vistaGuardada.modulo as VistaDeLaAplicacion | undefined) ?? 'registro';
  });
  const [casoEnviado, setCasoEnviado] = useState<CasoEnviado | null>(null);
  // HU-17 (`ui/MG-ILE-43.md`): aprobar u observar vuelve a la cola con el aviso de éxito.
  const [decisionDelAprobador, setDecisionDelAprobador] = useState<DecisionDelAprobador | null>(null);
  // HU-27 · «Ver compromisos» desde el Expediente: Disposiciones y seguimiento solo de ese caso.
  const [compromisosDe, setCompromisosDe] = useState<{ casoId: string; numero: string } | null>(null);
  // HU-37: el caso cuya trazabilidad está abierta. Se llega desde su expediente y se vuelve a él.
  const [trazabilidadDe, setTrazabilidadDe] = useState<string | null>(null);
  // El expediente abierto DESDE LA BANDEJA (T-37): un caso arbitrario, no el que
  // se acaba de enviar. Tiene prioridad mientras exista; enviar un caso lo limpia
  // para que la confirmacion muestre el expediente recien nacido y no uno viejo.
  const [expedienteDe, setExpedienteDe] = useState<string | null>(vistaGuardada?.expedienteDe ?? null);
  // HU-12/HU-15/HU-43: el caso abierto para EVALUAR desde la bandeja del Comite (o desde
  // «mis invitaciones» si la sesion es de un especialista, o desde la cola del aprobador
  // para revisar el informe). Se guarda con la pestaña que la bandeja pidió abrir y de
  // dónde vino, para que «Volver» regrese a esa pantalla.
  // De que vista se abrio el expediente, para que «Volver» regrese ahi.
  const [expedienteDesde, setExpedienteDesde] = useState<VistaDeLaAplicacion | null>(
    (vistaGuardada?.expedienteDesde as VistaDeLaAplicacion | null | undefined) ?? null,
  );
  const [evaluacionDe, setEvaluacionDe] = useState<{ casoId: string; pestana: PestanaDeLaEvaluacion; desde: 'evaluacion' | 'aprobacion' } | null>(
    (vistaGuardada?.evaluacionDe as { casoId: string; pestana: PestanaDeLaEvaluacion; desde: 'evaluacion' | 'aprobacion' } | null | undefined) ?? null,
  );

  // Lo que se persiste: el módulo y los casos abiertos, cada vez que cambian.
  useEffect(() => {
    guardarVista({ modulo, evaluacionDe, expedienteDe, expedienteDesde });
  }, [modulo, evaluacionDe, expedienteDe, expedienteDesde]);
  // Se lee UNA vez al montar. Releerlo en cada render haria reaparecer el aviso
  // despues de descartarlo, porque el espejo sigue en `localStorage` hasta que se
  // envie el caso.

  // AL RECARGAR SE VUELVE AL BORRADOR QUE SE ESTABA EDITANDO (CP-06-05, 3-sep-2026).
  //
  // `borradorDeLaPestana()` vive en `sessionStorage`: hay valor si esta pestana ya
  // estaba registrando y se recargo, y NO lo hay al abrir la aplicacion en una pestana
  // nueva. Esa es justo la distincion que faltaba, y por eso no contradice la decision
  // del operador del 28-ago-2026: el registro sigue abriendo limpio cuando se ENTRA, y
  // la bandeja sigue siendo el unico sitio donde se ELIGE que borrador retomar. Lo que
  // se recupera aqui no es una propuesta, es donde ya estabas.
  //
  // Sin esto, recargar montaba el formulario en blanco y seguir escribiendo abria un
  // SEGUNDO caso: el mismo hecho partido en dos registros, sin que nadie lo notara.
  //
  // Se siembra como `retomar` a secas, sin `guardado`: `RegistroConectado` pide el caso al
  // servidor, rellena el formulario con lo que devuelve y, si ya no existe, abre uno limpio.
  //
  // BUG-370: la ruta profunda va primero. El apuntador solo se escribia al NACER el
  // borrador, asi que un borrador RETOMADO no dejaba rastro y la recarga lo perdia; ahora
  // retomar escribe los dos, y la ruta sirve ademas a una pestana sin apuntador.
  const [retomar, setRetomar] = useState<{ casoId: string; guardado: unknown } | null>(() => {
    const casoId = borradorDeLaUrl ?? borradorDeLaPestana();
    return casoId ? { casoId, guardado: null } : null;
  });

  // La barra de direcciones dice QUE borrador esta abierto mientras el registro lo muestre,
  // y deja de decirlo en cuanto el registro es otra cosa: salir por el menu, «Nuevo caso»,
  // enviar o cerrar sesion. Si no se olvidara, recargar desde la bandeja volveria al
  // registro. En la pantalla de entrada no se toca: la ruta tiene que sobrevivir al ingreso.
  useEffect(() => {
    if (modulo === 'registro' && retomar) recordarRutaDelBorrador(retomar.casoId);
    else olvidarRutaDelBorrador();
  }, [modulo, retomar]);

  // ENLACE PROFUNDO DEL CORREO (HU-10): `/casos/<uuid>` -> el expediente de ese
  // caso. Se lee UNA vez al montar; se consume tras elegir rol (el login simulado
  // sigue siendo la puerta) y directo si la sesion ya existia en la pestana.
  // Si el expediente aun no nacio (el correo puede ganarle al despachador), la
  // pantalla de Expediente ya sabe esperar y reintentar sola.
  //
  // EL ENLACE NO LLEVA AL MISMO SITIO A TODOS LOS ROLES (bug 653 · CP-43-01 p.4). El correo
  // de invitacion al especialista (HU-43-C1) transporta ESTA misma ruta -el backend solo
  // sabe componer `<base>/casos/<uuid>`-, pero el invitado NO puede abrir el expediente: el
  // guardian de lectura de casos no admite su rol y el enlace del correo moria en un 403,
  // sin nada que hacer en pantalla. Donde si entra -y es justo a lo que se le invito- es a
  // la EVALUACION del caso: `GET /evaluacion/casos/{id}` lo deja pasar comprobando su
  // invitacion (RN-31) y la pantalla se pinta en solo lectura por `esInvitado`. Los demas
  // roles siguen aterrizando en el expediente, que es lo que su aviso promete.
  const [casoDeLaUrl, setCasoDeLaUrl] = useState(() => casoDesdeLaRuta(window.location.pathname));
  useEffect(() => {
    if (!casoDeLaUrl) return;
    if (usuario.rol === 'especialista') {
      // La pestaña de investigacion es donde el especialista aporta (HU-43-C5).
      setEvaluacionDe({ casoId: casoDeLaUrl, pestana: 'investigacion', desde: 'evaluacion' });
      setModulo('evaluacion_del_caso');
    } else {
      setExpedienteDe(casoDeLaUrl);
      setModulo('expediente');
    }
    setCasoDeLaUrl(null); // consumido: la navegacion interna vuelve a mandar
  }, [usuario, casoDeLaUrl]);

  // ENLACE DEL CORREO AL APROBADOR (HU-17-C14, `ui/MG-ILE-43.md`): `?vista=aprobacion&caso=<uuid>`
  // abre el informe de ese caso en la vista de aprobación, tras el ingreso. Solo si el rol
  // tiene el módulo; si no, se aterriza como siempre. Se consume una vez y sale de la barra.
  const [aprobacionDeLaUrl, setAprobacionDeLaUrl] = useState(() => aprobacionDesdeLaRuta(window.location.search));
  useEffect(() => {
    if (!aprobacionDeLaUrl) return;
    // El módulo lo concede el servidor (`modulos_permitidos`), no una lista por rol del frontend.
    if (permitidos.includes('aprobacion')) {
      setEvaluacionDe({ casoId: aprobacionDeLaUrl, pestana: 'informe', desde: 'aprobacion' });
      setModulo('evaluacion_del_caso');
    }
    olvidarRutaDeLaAprobacion();
    setAprobacionDeLaUrl(null);
  }, [permitidos, aprobacionDeLaUrl]);

  // SIN ROUTER, CAMBIAR DE PANTALLA NO MUEVE EL SCROLL. La bandeja se lee al fondo, se
  // abre un caso y la evaluación aparece con el pie a la vista. El contenedor que se
  // desplaza es el `<main>` del shell (la ventana no crece); se sube ese y, por si acaso,
  // la ventana. Va ANTES del `return` condicional: los hooks no pueden ir después.
  useEffect(() => {
    // jsdom no implementa `scrollTo`: se comprueba antes de llamar.
    const principal = document.querySelector('main');
    if (principal && typeof principal.scrollTo === 'function') principal.scrollTo({ top: 0 });
    if (typeof window.scrollTo === 'function') window.scrollTo({ top: 0 });
  }, [modulo, expedienteDe, evaluacionDe]);

  // BUG-376 (spec MG-ILE-376): LA GUARDA DE SALIDA DEL MENU LATERAL. El registro la presta
  // mientras esta montado (`registrarGuardaDeSalida`) y el menu pasa por ella: con cambios
  // sin guardar abre el aviso «Tienes cambios sin guardar» del propio registro y solo
  // navega con «Salir de todos modos». Sin guarda (cualquier otra pantalla) navega directo.
  // Un `ref` y no estado: prestarla no tiene que repintar la aplicacion.
  const guardaDeSalida = useRef<((irse: () => void) => void) | null>(null);
  const registrarGuardaDeSalida = useCallback((guarda: ((irse: () => void) => void) | null) => {
    guardaDeSalida.current = guarda;
  }, []);
  const irDesdeElMenu = (destino: ModuloDeNavegacion) => {
    // Entrar por el menú es entrar a la lista entera, sin el caso del expediente.
    if (destino === 'seguimiento') setCompromisosDe(null);
    const guarda = guardaDeSalida.current;
    // Pulsar la entrada de la pantalla en la que ya se esta no es salir de ella.
    if (!guarda || destino === modulo) { setModulo(destino); return; }
    guarda(() => setModulo(destino));
  };

  const alQuedarRegistrado = (respuesta: CasoEnviado) => {
    setCasoEnviado(respuesta);
    setExpedienteDe(null);
    setExpedienteDesde(null);
    // El caso dejo de ser borrador: «Registrar caso» no puede volver a abrirlo como tal
    // (spec MG-ILE-370, Navegacion). Sin esto, el registro lo reabria y autoguardaba
    // contra un caso ya enviado.
    setRetomar(null);
    setModulo('expediente');
  };

  const abrirExpedienteDe = (casoId: string) => {
    setExpedienteDe(casoId);
    // El origen no hace falta pasarlo desde cada pantalla: es la vista que esta
    // abierta en este momento, que es exactamente de donde viene quien pulsa.
    setExpedienteDesde(modulo);
    setModulo('expediente');
  };

  // «Cerrar sesión» (HU-32a-C8): la raíz borra del equipo los espejos del borrador y lo que la
  // aplicación guardó en la pestaña (`lib/sesion.ts::borrarDatosLocales`) y, con Entra, sale por
  // `logoutRedirect`. Aquí se olvida además lo que vive fuera del almacenamiento: la ruta del
  // borrador en la barra de direcciones, para que quien entre después en esta pestaña no
  // aterrice en el borrador de otro. Los borradores del servidor no se tocan.
  const alCerrarSesion = () => {
    olvidarVista();
    olvidarRutaDelBorrador();
    // Los filtros de las bandejas son del usuario, no de la pestaña.
    olvidarFiltros();
    // La aplicación se desmonta al cerrar la sesión (HU-32a): el caso enviado, los compromisos
    // filtrados y el borrador retomado se van con ella, sin limpiarlos uno a uno.
    onCerrarSesion();
  };

  const puedeRegistrar = permitidos.includes('registro');
  const abrirEvaluacion = (casoId: string, pestana: PestanaDeLaEvaluacion = 'investigacion', desde: 'evaluacion' | 'aprobacion' = 'evaluacion') => {
    setEvaluacionDe({ casoId, pestana, desde });
    setModulo('evaluacion_del_caso');
  };

  // QUE PANTALLA PINTA CADA VISTA. Una tabla y no una cascada de `else if`: con nueve
  // ramas, la cascada cruzo el limite de complejidad cognitiva que vigila el analisis de
  // calidad. Cada entrada se lee sola; una vista que no esta en la tabla —o que no tiene
  // con que pintarse, como la evaluacion sin caso abierto— cae en `SinPantallaTodavia`.
  const pantallas: Partial<Record<VistaDeLaAplicacion, () => ReactNode>> = {
    // El banner "tienes un caso a medio registrar" NO existe mas (decision del
    // operador, 28-ago-2026): con la bandeja respaldada por el servidor, retomar
    // tiene UN solo camino — Bandeja -> Retomar — y el registro siempre abre
    // limpio. El espejo local sigue vivo por debajo (rehidratacion y cortes de
    // red), pero ya no es una puerta de entrada.
    // Sin fragmento envolvente: aqui hubo dos hijos —el banner de "tienes un caso a
    // medio registrar" acompanaba al formulario— y al retirarse el banner el `<>…</>`
    // quedo envolviendo a uno solo, que no envuelve nada.
    registro: () => (
      <RegistroConectado
        // `key` fuerza un formulario limpio al cambiar de caso: sin ella, retomar
        // un borrador dejaria en pantalla el estado del anterior.
        key={retomar?.casoId ?? 'nuevo'}
        usuario={usuario}
        retomar={retomar}
        // ⚠ DECIA `'destinatarios'` desde PR 100 (corregido el 9-sep-2026). El boton
        // se llama "Volver a la bandeja" y llevaba al mantenimiento de destinatarios de
        // avisos, que es otro modulo y ademas de administracion: quien registraba un caso
        // acababa en una pantalla que no habia pedido y que muchos roles ni deberian ver.
        // Es el unico `onSalir` de esta pantalla, asi que afectaba a las TRES salidas que
        // pasan por el —el boton, el aviso de duplicado y el de cambios sin guardar—.
        onSalir={() => setModulo('bandeja')}
        alQuedarRegistrado={alQuedarRegistrado}
        // CP-07-40: desde el aviso de duplicado se puede IR al caso existente.
        // Recien ahora hay a donde ir: el expediente acepta un caso arbitrario.
        onAbrirCaso={abrirExpedienteDe}
        registrarGuardaDeSalida={registrarGuardaDeSalida}
      />
    ),
    bandeja: () => (
      <Bandeja
        onRetomar={(casoId: string, guardado: unknown) => {
          // Sembrar `retomar` y dejar que `RegistroConectado` se rehidrate
          // (contenido del espejo, version del servidor). La bandeja ya dejo el
          // espejo sembrado — es EL camino de retomar, el unico.
          //
          // BUG-370: y se anota en la pestana, como al nacer el borrador. Sin esto la
          // recarga no sabia que borrador estaba abierto y montaba el registro en blanco.
          // La ruta profunda la escribe el efecto de arriba.
          recordarEnLaPestana(casoId);
          setRetomar({ casoId, guardado });
          setModulo('registro');
        }}
        onVerExpediente={abrirExpedienteDe}
        onNuevo={puedeRegistrar ? () => {
          // Un caso NUEVO, aunque hubiera un retomado en curso: sin limpiar
          // `retomar`, "Nuevo caso" volveria a abrir el borrador anterior. Y sin
          // olvidar el apuntador de la pestana, la siguiente recarga lo resucitaria.
          olvidarBorradorDeLaPestana();
          setRetomar(null);
          setModulo('registro');
        } : undefined}
      />
    ),
    // El «Salir» del registro sigue llevando a la pantalla SUELTA, sin las pestañas del
    // módulo: es un atajo a los destinatarios, no una entrada a Administración.
    destinatarios: () => <Destinatarios />,
    // Administración ya tiene dos pantallas (HU-47 destinatarios, HU-23 autoridades y
    // plazos), así que la entrada del menú abre el módulo y no una de ellas. Abre en
    // destinatarios, que es lo que abría antes.
    admin: () => <Administracion />,
    // HU-11 (Sprint 2): la bandeja del Comité en cinco bloques. «Abrir» muestra el caso en
    // SOLO LECTURA (C21: el expediente con el Formato Verde); «Evaluar» abre la evaluación
    // (HU-12/15/43). Un especialista invitado ve, en cambio, solo sus invitaciones (HU-43).
    evaluacion: () => (usuario.rol === 'especialista'
      ? <MisInvitaciones onAbrir={(casoId) => abrirEvaluacion(casoId)} />
      : (
        <BandejaDelComite
          onAbrirCaso={abrirExpedienteDe}
          onEvaluarCaso={(casoId, pestana) => abrirEvaluacion(casoId, pestana)}
        />
      )),
    // HU-16 / HU-18 (Sprint 2): la cola del aprobador. «Revisar informe» abre la evaluación
    // en solo lectura, en la pestaña del FAR-03, con la tarjeta «Aprobación».
    aprobacion: () => (
      <AprobacionDeInformes
        onAbrirCaso={abrirExpedienteDe}
        onRevisar={(casoId) => { setDecisionDelAprobador(null); abrirEvaluacion(casoId, 'informe', 'aprobacion'); }}
        exito={decisionDelAprobador}
        onCerrarExito={() => setDecisionDelAprobador(null)}
        onIrABandeja={() => setModulo('bandeja')}
      />
    ),
    // HU-34b (Sprint 3): la foto del estado de la farmacovigilancia. Cada caso que nombra abre su
    // expediente, y «Volver» regresa aquí. Sin el módulo de la bandeja no se ofrece ir a ella.
    dashboard: () => (
      <Dashboard onAbrirCaso={abrirExpedienteDe}
        onIrABandeja={permitidos.includes('bandeja') ? () => setModulo('bandeja') : undefined}
        onSalir={() => setModulo(aterrizaje)} />
    ),
    // HU-35 (Sprint 3): el tablero de indicadores. Un caso del detalle abre su expediente, y
    // «Volver» regresa al tablero con el periodo y el país (los guarda la pantalla).
    indicadores: () => (
      <Indicadores onAbrirCaso={abrirExpedienteDe} onSalir={() => setModulo(aterrizaje)} />
    ),
    // HU-37 (Sprint 3): la trazabilidad del caso, abierta desde su expediente.
    trazabilidad: () => (trazabilidadDe ? (
      <Trazabilidad key={trazabilidadDe} casoId={trazabilidadDe}
        textoDeVolver="Volver al expediente" onVolver={() => setModulo('expediente')} />
    ) : null),
    // Sin caso abierto no hay evaluación que pintar: `null` cae en `SinPantallaTodavia`.
    evaluacion_del_caso: () => (evaluacionDe ? (
      <EvaluacionDelCaso
        // `key`: otro caso u otra pestaña inicial montan la pantalla limpia.
        key={`${evaluacionDe.casoId}-${evaluacionDe.pestana}`}
        casoId={evaluacionDe.casoId}
        actor={usuario.nombre}
        esInvitado={usuario.rol === 'especialista'}
        puedeAprobar={evaluacionDe.desde === 'aprobacion'}
        // HU-17-C9: decidir es del aprobador. La sesión simulada llega al servidor con todos
        // los roles, así que el Comité o el administrador que entren por la cola solo leen.
        puedeDecidir={evaluacionDe.desde === 'aprobacion' && tieneRol(usuario, 'aprobador')}
        onDecidido={(d) => { setDecisionDelAprobador(d); setModulo('aprobacion'); }}
        pestanaInicial={evaluacionDe.pestana}
        onVolver={() => setModulo(evaluacionDe.desde)}
        onVerExpediente={abrirExpedienteDe}
      />
    ) : null),
    // HU-50 / HU-24 (Sprint 2): la bandeja de Asuntos Regulatorios en cinco bloques con los dos
    // relojes del plazo regulatorio. Cada fila abre la tarea que toca; «Abrir» muestra el caso
    // en solo lectura (el expediente). `actor` firma la presentación, el cargo y el cierre.
    // `puedeCompletarEnElCaso`: la fecha de término y los datos del Amarillo que el Verde no
    // captura se escriben en el CASO, y esa puerta la abre el servidor solo a Asuntos
    // Regulatorios y al administrador. El Representante del País ve el documento sin el control.
    notificacion: () => (
      <BandejaRegulatoria onAbrirCaso={abrirExpedienteDe} actor={usuario.nombre}
        puedeCompletarEnElCaso={tieneRol(usuario, 'asuntos_regulatorios') || tieneRol(usuario, 'administrador')} />
    ),
    // HU-27 · HU-25 (Sprint 3): la lista única de compromisos del ámbito y «Registrar disposición».
    seguimiento: () => (
      <DisposicionesYSeguimiento onAbrirCaso={abrirExpedienteDe} casoFiltrado={compromisosDe}
        onQuitarCaso={() => setCompromisosDe(null)} />
    ),
    expediente: () => (
      <PanelDeExpediente
        expedienteDe={expedienteDe}
        casoEnviado={casoEnviado}
        onIrABandeja={() => setModulo('bandeja')}
        volverA={expedienteDesde ? etiquetaDeLaVista(expedienteDesde) : null}
        onVolver={expedienteDesde ? () => setModulo(expedienteDesde) : undefined}
        onVerCompromisos={permitidos.includes('seguimiento')
          ? (casoId, numero) => { setCompromisosDe({ casoId, numero }); setModulo('seguimiento'); }
          : undefined}
        onVerTrazabilidad={(casoId) => { setTrazabilidadDe(casoId); setModulo('trazabilidad'); }}
      />
    ),
  };
  const contenido = pantallas[modulo]?.() ?? <SinPantallaTodavia vista={modulo} sesion={sesion} onIrA={setModulo} />;

  return (
    <ProveedorDeCatalogos>
      <AppShell
        usuario={usuario}
        sesion={sesion}
        // Las vistas internas no tienen entrada en el menu: no se resalta ninguna.
        modulo={ES_DEL_MENU(modulo) ? modulo : undefined}
        onModulo={irDesdeElMenu}
        onLogout={alCerrarSesion}
      >
        <div className="mt-4">{contenido}</div>
      </AppShell>
    </ProveedorDeCatalogos>
  );
}

/**
 * LA RAÍZ: el ciclo de la sesión (HU-32a, MG-ILE-63). Sin sesión resuelta no hay menú que armar
 * ni actor que firme los borradores: se muestra la entrada, la carga, el error o «Sin acceso».
 * Con la sesión lista, la aplicación, y encima el aviso de sesión caducada si alguna petición
 * respondió 401 (HU-32a-C5).
 */
export default function App() {
  const control = useSesion();
  const { estado } = control;

  if (estado.fase === 'entrada') {
    return control.modo === 'simulado'
      ? <LoginSimulado onEntrar={control.entrarSimulado} />
      : <Entrada estado="entrada" onIngresar={control.ingresar} onReintentar={control.reintentar} />;
  }
  if (estado.fase === 'preparando' || estado.fase === 'cargando') {
    return <Entrada estado="cargando" simulada={control.modo === 'simulado'} onReintentar={control.reintentar} />;
  }
  if (estado.fase === 'error') {
    return <Entrada estado="error" simulada={control.modo === 'simulado'} onReintentar={control.reintentar} />;
  }
  if (estado.fase === 'sin_acceso') {
    return (
      <SinAcceso
        // HU-32a-C4: el texto lo arma el servidor. El respaldo es el comienzo literal del propio
        // criterio, solo por si llegara nulo (el contrato lo admite).
        texto={estado.sesion?.texto_sin_acceso ?? 'Tu cuenta no tiene acceso asignado.'}
        onCerrarSesion={control.cerrarSesion}
      />
    );
  }

  return (
    <>
      <div data-testid="sesion-exito" className="contents">
        <AplicacionConSesion
          // Otra persona (o la misma tras volver a entrar) monta la aplicación limpia.
          key={`${estado.usuario.rol}·${estado.sesion.texto_de_la_cabecera}`}
          sesion={estado.sesion}
          usuario={estado.usuario}
          onCerrarSesion={control.cerrarSesion}
        />
      </div>
      <Modal
        open={control.caducada}
        data-testid="sesion-expirada"
        eyebrow="Sesión"
        title={MENSAJE_DE_SESION_CADUCADA}
        tone="warning"
        footer={(
          <Button onClick={control.volverAIngresar} data-testid="sesion-expirada-ingresar">Volver a ingresar</Button>
        )}
      >
        <p>Al volver a ingresar regresas a esta misma pantalla.</p>
      </Modal>
    </>
  );
}
